import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { IApiQueryOptions } from 'api/ApiClient';
import { IUserProps } from 'components';
import { useDebounce } from 'hooks';
import { SelectUser } from 'modals';
import { clearRequest, selectAPIRequestStatus } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { Employees, getEmployeesRequest, selectEmployees } from 'store/Collections/Employees';
import { selectCurrentEmployeeAdminSites } from 'store/Employers';
import { IStore } from 'store/types';
import { AffiliationType, ApiQueryOperator, EmployeeStatus, EmployeeType, IEmployee, RequestStatus } from 'types';
import { capitalize } from 'utils/strings';

interface AddExistingUserProps {
  active: boolean;
  onSelect: (emp: IEmployee) => void;
  onClose: () => void;
  limit: number;
  setLimit: (lim: number) => void;
  name: string;
  setName: (name: string) => void;
}

const AddExistingUser = ({ active, onSelect, onClose, limit, setLimit, name, setName }: AddExistingUserProps) => {
  const dispatch = useDispatch();
  const currentOrganizationId = useSelector(selectCurrentEmployee())?.organizationId;
  const getEmployeesStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, Employees.GET_EMPLOYEES_REQUEST));
  const availableAdminSites = useSelector(selectCurrentEmployeeAdminSites);
  const foundEmployees = useSelector(selectEmployees())?.data || [];
  const [displayEmployees, setDisplayEmployees] = useState<IUserProps[]>();
  const pageSize = 4;

  const loadMore = () => {
    setLimit(limit + pageSize);
  };

  const debouncedSetName = useDebounce(setName);

  const onNameChange = (name: string) => {
    debouncedSetName(name.trim());
  };

  const fetchEmployees = (orgId: string, limit: number, name: string) => {
    const options: IApiQueryOptions = {
      limit: limit + pageSize + 1,
      sort: 'name',
      q: !isEmpty(name) ? [{ key: 'name', operator: ApiQueryOperator.like, value: name }] : [],
      addExisting: true,
    };
    dispatch(getEmployeesRequest(AffiliationType.ORGANIZATIONS, orgId, options));
  };

  useEffect(() => {
    if (currentOrganizationId) {
      fetchEmployees(currentOrganizationId, limit, name);
    }
  }, [currentOrganizationId, limit, name]);

  useEffect(() => {
    if (currentOrganizationId && getEmployeesStatus === RequestStatus.SUCCESS) {
      setDisplayEmployees(mapEmployees(foundEmployees));
    }
  }, [currentOrganizationId, getEmployeesStatus]);

  useEffect(() => () => dispatch(clearRequest(Employees.GET_EMPLOYEES_REQUEST)), []);

  const mapEmployees = useCallback(
    (employees: IEmployee[]) =>
      employees?.map((employee) => {
        const sites = employee.sites.filter((s) => s.status !== EmployeeStatus.DISMISSED);
        const siteText: string = (() => {
          if (sites.length > 1 || sites.length === 0) {
            if (employee.type === EmployeeType.ADMIN) return 'All Sites';
            return `${sites.length} Sites`;
          }
          if (sites.length === 1) {
            return sites[0].name;
          }
          return '';
        })();
        const roleText: string = (() => {
          const roles = sites.map((site) => site.role);
          const uniqueRoles = new Set(roles);
          if (uniqueRoles.size > 1 || uniqueRoles.size === 0) {
            return `${sites.length} Roles`;
          }
          if (uniqueRoles.size === 1) {
            return capitalize(Array.from(uniqueRoles)[0]);
          }
          return '';
        })();

        const siteIds = sites.map((s) => s.id.toString());

        const shouldDisableBtn = employee.type === EmployeeType.ADMIN || availableAdminSites.every((site) => siteIds.includes(site.id));

        return {
          id: employee.id,
          username: employee.name,
          image: employee.user?.avatarUrl,
          hasPaymentMethod: !!employee.user?.card,
          role: roleText,
          site: siteText,
          disabled: shouldDisableBtn,
        };
      }),
    [availableAdminSites]
  );

  return (
    <SelectUser
      users={displayEmployees}
      isActive={active}
      onSelect={(id) => onSelect(foundEmployees?.find((emp) => emp.id === id))}
      onNameChange={onNameChange}
      loadMore={loadMore}
      onClose={() => {
        setLimit(pageSize);
        setName('');
        onClose();
      }}
      limit={limit}
      isLoading={!displayEmployees}
      disabledToolTip="User already has access to all sites"
    />
  );
};

export default AddExistingUser;
